import React, {useEffect, useState} from 'react';
import {AccentColorOptions, useBreakpointDown} from 'epic-ue-theme';
import styled from 'styled-components';
import {FiltersBarMobile} from './FiltersBarMobile';
import {FiltersBarDesktop} from './FiltersBarDesktop';
import root from 'window-or-global';
import {VerticalSpacer} from '../spacer/VerticalSpacer';
import {CurrentFilterTags} from './CurrentFilterTags';
import {useFilterFns} from './filters.callbacks';

const Styled = styled.div`
    ${(p) => p.theme.breakpoints.up('md')} {
        .mobile-filter-section {
            display: none;
        }
    }
`;

export const TagPills = styled.div`
    position: relative;
    z-index: 1;
    display: flex;
    flex-wrap: wrap;
    min-height: 32px;
    font-size: 14px;

    .tag-pill {
        margin-top: 5px;
        background-color: ${(p) => p.theme.palette.neutral.pearl};
        color: ${(p) => p.theme.palette.common.black};
    }

    ${(p) => p.theme.breakpoints.down('sm')} {
        .filter-results-count {
            text-align: center;
            margin-top: 15px;
        }
    }
`;

export type CustomContains<T> = (filterName: string, item: T, filterValueList: string[]) => boolean;
export type SearchFilterFn<T> = (fullResultsList: T[], search: string) => T[];
export type SetSearchFn = (value: string) => void;
export type SetResultsListFn<T> = (value: T[]) => void;
export interface SelectedFilters {
    [key: string]: string[];
}

export interface FilterOption<T> {
    label: string;
    name: string;
    checkboxOptions?: string[];
    customContains?: CustomContains<T>;
    onClick?: (
        e: React.MouseEvent<HTMLLIElement, MouseEvent>,
        filterValue: string,
        checked: boolean
    ) => void;
}

export interface FiltersBarProps<T> {
    accentColor?: AccentColorOptions;
    resultsList?: T[];
    fullResultsList?: T[];
    filterOptions?: FilterOption<T>[];
    filteredDataLabel?: string;
    filteredDataLabelPlural?: string;
    desktopSearchLabel?: string;
    mobileSearchLabel?: string;
    search?: string;
    searchFilterFn?: SearchFilterFn<T>;
    setSearch: SetSearchFn;
    setResultsList?: SetResultsListFn<T>;
    totalCount?: number;
    onRemoveTag?: (e, filterLabel, value, isChecked) => void;
    initSelectedFilters?: SelectedFilters;
    filterLabel?: string;
    noBottomSpacing?: boolean;
    mobileFilterTitle?: string;
    totalCountIsPreferredCount?: boolean;
}

export const FiltersBar = <T,>({
    accentColor = 'sky',
    resultsList,
    fullResultsList = [],
    filterOptions,
    filteredDataLabel = 'item',
    filteredDataLabelPlural = 'items',
    desktopSearchLabel,
    mobileSearchLabel,
    search,
    searchFilterFn,
    setSearch,
    setResultsList,
    totalCount,
    onRemoveTag,
    initSelectedFilters = {},
    filterLabel,
    noBottomSpacing,
    mobileFilterTitle,
    totalCountIsPreferredCount
}: FiltersBarProps<T>): JSX.Element => {
    const isSm = useBreakpointDown('sm');
    const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>(initSelectedFilters);
    const [openedFilterBarMobile, setOpenedFilterBarMobile] = useState(false);

    const hasFiltersSelected = Object.keys(selectedFilters).length !== 0;

    //Prevent background body scroll, when filters overlay is opened on Mobile
    useEffect(() => {
        isSm && openedFilterBarMobile
            ? (root.document.body.style.overflow = 'hidden')
            : (root.document.body.style.overflow = 'initial');
    }, [openedFilterBarMobile]);

    const {filterResultList, updateSelectedFilters} = useFilterFns(
        selectedFilters,
        setSelectedFilters,
        setResultsList,
        fullResultsList,
        search,
        searchFilterFn,
        filterOptions
    );

    return (
        <Styled id="filtersBar">
            <FiltersBarMobile
                accentColor={accentColor}
                resultsList={resultsList}
                filteredDataLabel={filteredDataLabel}
                filteredDataLabelPlural={filteredDataLabelPlural}
                filterOptions={filterOptions}
                onFilterSelect={filterResultList}
                setSearch={setSearch}
                search={search}
                setOpenedFilterBarMobile={setOpenedFilterBarMobile}
                updateSelectedFilters={updateSelectedFilters}
                totalCount={totalCount}
                totalCountIsPreferredCount={totalCountIsPreferredCount}
                mobileSearchLabel={mobileSearchLabel}
                filterLabel={filterLabel}
            />

            <FiltersBarDesktop
                accentColor={accentColor}
                resultsList={resultsList}
                filteredDataLabel={filteredDataLabel}
                filteredDataLabelPlural={filteredDataLabelPlural}
                desktopSearchLabel={desktopSearchLabel}
                filterOptions={filterOptions}
                onFilterSelect={filterResultList}
                setSearch={setSearch}
                search={search}
                setOpenedFilterBarMobile={setOpenedFilterBarMobile}
                openedFilterBarMobile={openedFilterBarMobile}
                selectedFilters={selectedFilters}
                updateSelectedFilters={updateSelectedFilters}
                totalCount={totalCount}
                totalCountIsPreferredCount={totalCountIsPreferredCount}
                noBottomSpacing={noBottomSpacing}
                mobileFilterTitle={mobileFilterTitle}
            />

            {/* Reserve empty space for CurrentFilterTags only when noBottomSpacing is not set  */}
            {(!noBottomSpacing || (noBottomSpacing && hasFiltersSelected)) && (
                <VerticalSpacer variant="eyebrow" />
            )}

            {(!noBottomSpacing || (noBottomSpacing && hasFiltersSelected)) && (
                <CurrentFilterTags
                    selectedFilters={selectedFilters}
                    removeFilter={updateSelectedFilters}
                    onRemoveTag={onRemoveTag}
                />
            )}

            {!noBottomSpacing && <VerticalSpacer variant="heading" />}
        </Styled>
    );
};
